import { useState, useMemo } from "react";
import { Shuffle, Check } from "lucide-react";
import { AVATAR_SETS, setKeyForUrl } from "@/lib/avatarSets";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

// Iter 140 — shared avatar gallery picker: themed face sets + the DiceBear
// cartoon styles. Pure value/onChange so it works both for "my own avatar"
// (EditAvatarDialog → PUT /me/avatar) and for an admin editing someone else
// (Users edit dialog → the user payload).
export const CARTOON_STYLES = [
  { key: "fun-emoji",  label: "Fun emoji" },
  { key: "avataaars",  label: "Avataaars" },
  { key: "bottts",     label: "Bottts" },
  { key: "adventurer", label: "Adventurer" },
  { key: "big-smile",  label: "Big smile" },
  { key: "lorelei",    label: "Lorelei" },
];

export function cartoonUrl(style, seed) {
  const s = encodeURIComponent(seed || "conduit");
  return `https://api.dicebear.com/9.x/${style}/svg?seed=${s}&backgroundType=gradientLinear&radius=50`;
}

export function styleFromAvatarUrl(url) {
  const m = /dicebear\.com\/[0-9.]+\/([^/]+)\/svg/.exec(url || "");
  return m ? m[1] : null;
}
export function seedFromAvatarUrl(url) {
  const m = /[?&]seed=([^&]+)/.exec(url || "");
  return m ? decodeURIComponent(m[1]) : null;
}

export function AvatarGalleryPicker({ value, onChange, seedName = "conduit", testid = "avatar" }) {
  const isCartoon = (value || "").includes("dicebear.com");
  const [setKey, setSetKey] = useState(() => setKeyForUrl(value) || (isCartoon ? "cartoon" : "mystery"));
  const [style, setStyle] = useState(() => styleFromAvatarUrl(value) || "fun-emoji");
  const [seed, setSeed] = useState(() => seedFromAvatarUrl(value) || seedName);
  const activeSet = AVATAR_SETS.find((s) => s.key === setKey);
  const previewSeed = useMemo(() => seed || seedName, [seed, seedName]);

  const pickStyle = (next) => { setStyle(next); onChange(cartoonUrl(next, seed)); };
  const shuffleSeed = () => {
    const next = `${seedName}-${Math.random().toString(36).slice(2, 8)}`;
    setSeed(next);
    onChange(cartoonUrl(style, next));
  };

  return (
    <div className="w-full space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-[10px] uppercase tracking-wide text-slate-500">Gallery</span>
        <Select value={setKey} onValueChange={setSetKey}>
          <SelectTrigger className="h-8 flex-1 text-xs" data-testid={`${testid}-set-select`}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {AVATAR_SETS.map((s) => (
              <SelectItem key={s.key} value={s.key} data-testid={`${testid}-set-${s.key}`}>{s.label}</SelectItem>
            ))}
            <SelectItem value="cartoon" data-testid={`${testid}-set-cartoon`}>Cartoon styles</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {activeSet ? (
        <div className="grid grid-cols-6 gap-2" data-testid={`${testid}-set-grid`}>
          {activeSet.images.map((url, i) => {
            const active = value === url;
            return (
              <button key={url} type="button" onClick={() => onChange(url)}
                data-testid={`${testid}-face-${activeSet.key}-${i}`} title="Use this face"
                className={`relative overflow-hidden rounded-full transition ${
                  active ? "ring-2 ring-primary ring-offset-1" : "ring-1 ring-slate-200 hover:ring-primary/50"}`}>
                <img src={url} alt={`${activeSet.label} ${i + 1}`} className="h-11 w-11 object-cover"
                  style={{ objectPosition: "center 28%", transform: "scale(1.12)" }} />
                {active && (
                  <span className="absolute inset-x-0 bottom-0 flex justify-center bg-primary/85 py-[1px]">
                    <Check className="h-2 w-2 text-primary-foreground" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      ) : (
        <div data-testid={`${testid}-style-grid`}>
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-wide text-slate-500">Cartoon style</span>
            <button type="button" onClick={shuffleSeed} data-testid={`${testid}-shuffle-seed`}
              className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline">
              <Shuffle className="h-3 w-3" /> New face
            </button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {CARTOON_STYLES.map((s) => {
              const active = s.key === style && isCartoon;
              return (
                <button key={s.key} type="button" onClick={() => pickStyle(s.key)}
                  data-testid={`${testid}-style-${s.key}`}
                  className={`group flex flex-col items-center gap-1 rounded-lg border px-2 py-2 transition ${
                    active ? "border-primary bg-primary/5 ring-2 ring-primary/40"
                           : "border-slate-200 bg-white hover:border-primary/40"}`}>
                  <div className="relative h-12 w-12 overflow-hidden rounded-full bg-slate-50">
                    <img src={cartoonUrl(s.key, previewSeed)} alt={s.label} className="h-full w-full object-cover" />
                    {active && (
                      <span className="absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-2.5 w-2.5" />
                      </span>
                    )}
                  </div>
                  <span className="text-[10px] font-medium text-slate-600">{s.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
